import React, { useState, useEffect } from 'react';
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton, IconButton } from '@fluentui/react/lib/Button';
import { TextField } from '@fluentui/react/lib/TextField';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { Callout } from '@fluentui/react/lib/Callout';
import { Stack } from '@fluentui/react/lib/Stack';
import { Link } from '@fluentui/react';
import Api from '../../../services/Api';
import { mapDbUserForPeoplePicker } from '../../formFields/PeoplePicker';
import { useSelector } from 'react-redux';

const RequestLicenseDialog = ({app, refresh, disableBtn, onSubmit}) => {
    const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);
    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
    const [licenseNumber, setLicenseNumber] = useState(0);
    const [costCenter, setCostCenter] = useState("");
    const [businessJustification, setBusinessJustification] = useState("");
    const iconButtonId = useId('iconButton');
    const currentlyLoggedUser = useSelector(state => state.user.currentUser);
    const isAdmin = useSelector(state => state.user.isAdmin);

    useEffect(() => {
        setLastCcAndBusinessJustification();
    }, [])

    const dialogContentProps = {
        type: DialogType.normal,
        title: 'Provide information for your request',
        closeButtonAriaLabel: 'Close',
        subText: 'Before you submit your request you need to provide additional information'
    }

    const stackTokens = {
        childrenGap: 4,
        maxWidth: 300,
    };

    const setLastCcAndBusinessJustification = () => {
        const latestTask = app.requestLicenseTasks.sort((a, b) => new Date(b.created) - new Date(a.created)).find(() => true);

        if (latestTask !== undefined) {
            setLicenseNumber(app.licensesAssigned);
            setCostCenter(latestTask.costCenter);
            setBusinessJustification(latestTask.businessJustification);
        }
    }

    const customLabel = (props: ITextFieldProps) => {
        const iconProps = { iconName: 'Info' };
        return (
            <React.Fragment>
                <Stack horizontal verticalAlign='center'>
                <span id={props.id}>{props.label}</span>
                <IconButton
                id={iconButtonId}
                iconProps={iconProps}
                title="Info"
                ariaLabel="Info"
                onClick={toggleIsCalloutVisible}
                /> 
                </Stack>
                {isCalloutVisible && (
                    <Callout
                        target={'#' + iconButtonId}
                        setInitialFocus
                        onDismiss={toggleIsCalloutVisible}
                        role="alertdialog"
                    >
                        <Stack tokens={stackTokens} horizontalAlign="start" styles={labelCalloutStackStyles}>
                            <span>If you don't know cost center please contact your business representative regarding this information</span>
                            <Link href="#">Link placeholder</Link>
                            <DefaultButton onClick={toggleIsCalloutVisible}>Close</DefaultButton>
                        </Stack>
                    </Callout>
                )}
            </React.Fragment>
        )
    };

    const submitRequest = () => {
        if (isNaN(licenseNumber) || !(costCenter.length > 0) || !(businessJustification.length > 0) || licenseNumber === app.licensesAssigned ) {
            return;
        }
        
        let owners = app.owners.map(x => mapDbUserForPeoplePicker(x));

        const post = {
            powerAppId: app.id,
            licensesAmountRequested: licenseNumber,
            costCenter: costCenter,
            businessJustification: businessJustification,
            approvers: owners,
            createdBy: currentlyLoggedUser
        }

        const url = "/CreateRequestLicenseTask";

        Api.post(url, post)
            .then(response => response.data)
            .then(post => {
                if(post === 200) {
                    onSubmit('licensesRequested');
                    toggleHideDialog();
                } else {
                    console.log("Request license failed");
                }
            })
            .catch((error) => {
            console.log(error)})
    }

    const confirmLicensesGranted = () => {
        let approvedTasks = app.requestLicenseTasks.filter(x => x.status === 2).sort((a, b) => new Date(b.created) - new Date(a.created));

        const post = {
            id: approvedTasks[0].id,
            status: 'LicensesGranted'
        }

        const url = "/ApproveRequestLicense";

        Api.post(url, post)
            .then(response => response.data)
            .then(post => {
                if (post === 200) {
                    refresh();
                    onSubmit('licensesGranted');
                } else {
                    console.log("Request license failed");
                }
            })
            .catch((error) => {
            console.log(error)})
    }

    
    const getNaNErrorMessage = (value: string): string => {
        return isNaN(value) ? 'Input value must be a number.' : '';
    }
    
    const getEmptyStringErrorMessage = (value: string): string => {
        return value.length > 0 ? '' : 'Input value cannot be empty';
    }
    
    const approvedTasksExist = app.requestLicenseTasks.filter(x => x.status === 2).length > 0;
    const activeTasksExist = app.requestLicenseTasks.filter(x => x.status === 1).length > 0;
 
    return(
        <div>
            {
                approvedTasksExist ? 
                isAdmin && <PrimaryButton text="Confirm licenses granted" onClick={confirmLicensesGranted} /> 
                :
                !activeTasksExist && <PrimaryButton secondaryText="Opens the Sample Dialog" onClick={toggleHideDialog} text="Request Per App licenses" disabled={disableBtn}/>
            }
            <Dialog
                hidden={hideDialog}
                onDismiss={toggleHideDialog}
                dialogContentProps={dialogContentProps}
            >
                <TextField styles={textFieldStyles} label="Current number of licenses assigned" 
                        placeholder="Please enter a number here" 
                        onChange={(v) => setLicenseNumber(v.target.value)} 
                        onGetErrorMessage={getNaNErrorMessage} 
                        validateOnLoad={false} 
                        defaultValue={app.licensesAssigned > 0 ? app.licensesAssigned : ''}/>

                <TextField label="Cost Center" onRenderLabel={customLabel} 
                        placeholder="Please enter text here" 
                        onChange={(v) => setCostCenter(v.target.value)} 
                        onGetErrorMessage={getEmptyStringErrorMessage} 
                        validateOnLoad={false} 
                        defaultValue={costCenter}/>

                <TextField label="Business Justification" multiline rows={3} 
                        placeholder="Please enter text here" 
                        onChange={(v) => setBusinessJustification(v.target.value)} 
                        onGetErrorMessage={getEmptyStringErrorMessage} 
                        validateOnLoad={false} 
                        defaultValue={businessJustification}/>

                <DialogFooter>
                    <PrimaryButton onClick={() => submitRequest(this)} text="Submit" />
                    <DefaultButton onClick={toggleHideDialog} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </div>
    )
}

const textFieldStyles = {
    fieldGroup: {
        "&:focus": {
            borderColor: '#742774'
          },
    }
}

const labelCalloutStackStyles = {
    root: {
        padding: 20
    }
}

export default RequestLicenseDialog;


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEditElements/RequestLicenseDialog.js